import { appStorage, isPrivateBeta } from '../lib/betaStorage'
import { betaSameSupportCircle } from '../beta/access'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  isGroupThreadId,
  seedAvailability,
  seedCoverRequests,
  seedDocuments,
  seedEvents,
  seedGoals,
  seedGroups,
  seedMessages,
  seedNotificationPrefs,
  seedTasks,
  seedUpdates,
  threadIdFor,
  users,
} from '../data/seed'
import { typeMeta } from '../lib/eventMeta'
import {
  buildCoverCandidates,
  isAvailableOnDate,
  needsCover,
  shiftDateLabel,
  shiftSummary,
} from '../lib/cover'
import type { CoverCandidate } from '../lib/cover'
import {
  handoverToText,
  linkableShiftsFor,
} from '../lib/handover'
import {
  accessFromPermissions,
  backfillParticipantPermissions,
  canAccessDocument,
  canReadHandovers,
  canRecordHandover,
  createTeamMemberAccess,
  defaultAccessProfile,
  defaultParticipantPermissions,
  hasPermission,
  resolvePermissionsForUser,
} from '../lib/permissions'
import { localISODate } from '../lib/date'
import type {
  AppPermission,
  AvailabilityPrefs,
  ChatMessage,
  CoverRequest,
  DocumentItem,
  DocumentationStyle,
  EventAssignee,
  Goal,
  GroupThread,
  HandoverNote,
  InterfaceMode,
  NotificationPrefs,
  ParticipantAccessProfile,
  ParticipantAccountType,
  ParticipantSpace,
  ScheduleEvent,
  SmsLogEntry,
  SoapNote,
  TaskItem,
  TeamMemberAccess,
  UpdatePost,
  User,
} from '../types'

const STORAGE_KEY = 'schub.state.v2'
const CURRENT_USER_KEY = 'schub.currentUserId'

interface PersistedState {
  events: ScheduleEvent[]
  tasks: TaskItem[]
  updates: UpdatePost[]
  documents: DocumentItem[]
  goals: Goal[]
  notificationPrefs: NotificationPrefs
  smsLog: SmsLogEntry[]
  messages: ChatMessage[]
  groups: GroupThread[]
  availability: AvailabilityPrefs[]
  /** In-app "can you cover this shift?" asks and their replies. */
  coverRequests: CoverRequest[]
  /** The participant-owned space: account preferences + who can access what. */
  participantSpace: ParticipantSpace
}

function isSupportWorkerUser(userId: string) {
  return users.find((u) => u.id === userId)?.role === 'support_worker'
}

function defaultParticipantSpace(): ParticipantSpace {
  const owner = users.find((u) => u.role === 'participant') ?? users[0]
  return {
    participantUserId: owner.id,
    accessProfile: defaultAccessProfile('adult', 'standard'),
    // Empty until the participant changes something: no record means
    // "role defaults apply", so evolving defaults reach everyone.
    teamAccess: [],
  }
}

function defaultState(): PersistedState {
  return {
    events: seedEvents,
    tasks: seedTasks,
    updates: seedUpdates,
    documents: seedDocuments,
    goals: seedGoals,
    notificationPrefs: seedNotificationPrefs,
    smsLog: [],
    messages: seedMessages,
    groups: seedGroups,
    // Availability is a support-worker rostering feature only.
    availability: seedAvailability.filter((a) => isSupportWorkerUser(a.userId)),
    coverRequests: seedCoverRequests,
    participantSpace: defaultParticipantSpace(),
  }
}

function looksCompatible(parsed: unknown): parsed is Partial<PersistedState> {
  if (!parsed || typeof parsed !== 'object') return false
  const p = parsed as Partial<PersistedState>
  if (p.events && !p.events.every((e) => Array.isArray(e.assignees))) return false
  if (p.messages && p.messages.some((m) => 'recipientId' in m)) return false
  return true
}

function loadState(): PersistedState {
  const defaults = defaultState()
  try {
    const raw = appStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (looksCompatible(parsed)) {
        const merged = { ...defaults, ...parsed }
        // Stored state written before the access architecture existed has no
        // space (or a partial one) — fill the gaps rather than discard it.
        const accountType =
          parsed.participantSpace?.accessProfile?.accountType ??
          defaults.participantSpace.accessProfile.accountType
        merged.participantSpace = {
          ...defaults.participantSpace,
          ...(parsed.participantSpace ?? {}),
          accessProfile: {
            ...defaults.participantSpace.accessProfile,
            ...(parsed.participantSpace?.accessProfile ?? {}),
            // Capabilities added after this space was saved are not the same
            // thing as capabilities the participant switched off.
            permissions: backfillParticipantPermissions(
              parsed.participantSpace?.accessProfile?.permissions ??
                defaults.participantSpace.accessProfile.permissions,
              accountType,
            ),
          },
          teamAccess: parsed.participantSpace?.teamAccess ?? defaults.participantSpace.teamAccess,
        }
        merged.coverRequests = parsed.coverRequests ?? defaults.coverRequests
        // Ignore and remove legacy availability belonging to allied health,
        // coordinators, participants or nominees. Only support workers can
        // participate in the shift-availability system.
        merged.availability = (parsed.availability ?? defaults.availability).filter((a) =>
          isSupportWorkerUser(a.userId),
        )
        return merged
      }
      appStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // ignore corrupt storage
  }
  return defaults
}

function niceDateLabel(dateISO: string) {
  return new Date(dateISO + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

export type EventUpdateChanges = Partial<
  Pick<ScheduleEvent, 'title' | 'type' | 'date' | 'startTime' | 'endTime' | 'notes' | 'calendarCategory' | 'calendarSymbol' | 'calendarTiming' | 'calendarAudience'>
> & { assigneeIds?: string[] }

interface AppContextValue extends PersistedState {
  currentUser: User
  allUsers: User[]
  setCurrentUserId: (id: string) => void
  addEvent: (event: ScheduleEvent) => void
  addTask: (task: TaskItem) => void
  toggleTaskStatus: (taskId: string) => void
  addUpdate: (update: UpdatePost) => void
  toggleChecklistItem: (updateId: string, itemId: string) => void

  /* --- Structured shift handover --------------------------------------- *
   * A handover is published as one post in the existing Team Updates feed,
   * so there is a single handover system and a saved note can never show up
   * twice. Free-text handovers written before this stage keep working: they
   * simply have no structured note attached.                                */
  /** Support shifts the signed-in worker could attach a handover to. */
  myLinkableShifts: ScheduleEvent[]
  /** Write a handover. Refused for anyone who is not a rostered worker. */
  recordHandover: (input: {
    note: HandoverNote
    documentationStyle: DocumentationStyle
    soap?: SoapNote
    linkedEventId?: string
    notifyBySms?: boolean
  }) => boolean
  /** Whether the signed-in user may open the handover writing screen. */
  canWriteHandover: boolean
  /** Updates this person is allowed to see — handovers respect view_handover. */
  visibleUpdates: UpdatePost[]
  /** Handovers only, newest first, for whoever may read them. */
  handoverUpdates: UpdatePost[]
  eventById: (eventId: string) => ScheduleEvent | undefined
  addDocument: (doc: DocumentItem) => void
  updateDocumentAccess: (docId: string, access: DocumentItem['access'], allowedUserIds: string[]) => void
  updateGoalProgress: (goalId: string, progressPercent: number) => void
  updateNotificationPrefs: (prefs: NotificationPrefs) => void
  logSms: (entry: SmsLogEntry) => void
  canSeeDocument: (doc: DocumentItem) => boolean
  sendMessage: (partnerId: string, text: string, eventId?: string, coverRequestId?: string) => void
  markThreadRead: (partnerId: string) => void
  unreadCountFrom: (partnerId: string) => number
  totalUnreadCount: number
  getAvailability: (userId: string) => AvailabilityPrefs
  updateAvailability: (prefs: AvailabilityPrefs) => void
  isAvailableOn: (userId: string, dateISO: string) => boolean
  /** Support workers showing a green availability dot for that day. */
  availableTeamMembersOn: (dateISO: string) => User[]
  respondToEvent: (eventId: string, status: 'accepted' | 'declined') => void
  updateEvent: (eventId: string, changes: EventUpdateChanges) => void
  deleteEvent: (eventId: string) => void
  createGroupThread: (name: string, participantIds: string[]) => string
  sendGroupMessage: (groupId: string, text: string, eventId?: string) => void
  markGroupThreadRead: (groupId: string) => void
  unreadCountForGroup: (groupId: string) => number

  /* --- Support worker availability & Needs Cover ------------------------ *
   * One shift record serves every interface mode: Standard, Simple and
   * Visual all read the same `cover` field and the same cover requests.     */
  coverRequests: CoverRequest[]
  /** "I can't do this shift" — hands the shift back as Needs Cover. */
  withdrawFromShift: (eventId: string, reason?: string) => void
  /** Ask chosen team members to cover. Never assigns anyone. */
  sendCoverRequests: (eventId: string, userIds: string[], note?: string) => void
  /** The asked worker's answer. Only an accept assigns the shift. */
  respondToCoverRequest: (requestId: string, response: 'accepted' | 'declined') => void
  coverRequestsForEvent: (eventId: string) => CoverRequest[]
  pendingCoverRequestsFor: (userId: string) => CoverRequest[]
  /** Team members who could cover a shift, with availability and clashes. */
  coverCandidatesForEvent: (eventId: string) => CoverCandidate[]

  /* --- Participant access & permissions ------------------------------- *
   * The participant (or their nominee) owns these. Everything the UI hides
   * or reveals reads from `permissions`; the same rules are mirrored in
   * lib/permissions.ts PERMISSION_BACKEND_MAP for future RLS policies.     */
  participantSpace: ParticipantSpace
  accessProfile: ParticipantAccessProfile
  /** Effective permissions for the signed-in user in this space. */
  permissions: AppPermission[]
  can: (permission: AppPermission) => boolean
  isSpaceOwner: boolean
  interfaceMode: InterfaceMode
  companionEnabled: boolean
  setAccountType: (accountType: ParticipantAccountType) => void
  setInterfaceMode: (mode: InterfaceMode) => void
  setCompanionEnabled: (enabled: boolean) => void
  setParticipantPermissions: (permissions: AppPermission[]) => void
  /** Permissions a given team member currently holds. */
  permissionsForUser: (userId: string) => AppPermission[]
  getTeamMemberAccess: (userId: string) => TeamMemberAccess
  /** Participant override: store an exact permission set for one member. */
  setTeamMemberPermissions: (userId: string, permissions: AppPermission[]) => void
  /** Drop the override so the member follows their role defaults again. */
  resetTeamMemberAccess: (userId: string) => void
}

const AppContext = createContext<AppContextValue | null>(null)

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PersistedState>(loadState)
  const [currentUserId, setCurrentUserIdState] = useState<string>(() => {
    return appStorage.getItem(CURRENT_USER_KEY) || ''
  })

  useEffect(() => {
    appStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])

  const setCurrentUserId = useCallback((id: string) => {
    appStorage.setItem(CURRENT_USER_KEY, id)
    setCurrentUserIdState(id)
  }, [])

  const currentUser = useMemo(
    () => users.find((u) => u.id === currentUserId) ?? users[0],
    [currentUserId],
  )

  /* --- Participant access & permissions -------------------------------- */

  const participantSpace = state.participantSpace
  const accessProfile = participantSpace.accessProfile
  const isSpaceOwner = currentUser.id === participantSpace.participantUserId

  const permissions = useMemo(
    () => resolvePermissionsForUser(currentUser, participantSpace).filter(permission => !isPrivateBeta || !['manage_team', 'manage_permissions', 'manage_funding', 'manage_onboarding'].includes(permission)),
    [currentUser, participantSpace],
  )

  const can = useCallback(
    (permission: AppPermission) => hasPermission(permissions, permission),
    [permissions],
  )

  const updateAccessProfile = useCallback((changes: Partial<ParticipantAccessProfile>) => {
    setState((s) => ({
      ...s,
      participantSpace: {
        ...s.participantSpace,
        accessProfile: { ...s.participantSpace.accessProfile, ...changes },
      },
    }))
  }, [])

  const setAccountType = useCallback(
    (accountType: ParticipantAccountType) => {
      // Switching account type re-applies that type's baseline, because the
      // adult and youth baselines differ on who holds the admin controls.
      updateAccessProfile({ accountType, permissions: defaultParticipantPermissions(accountType) })
    },
    [updateAccessProfile],
  )

  const setInterfaceMode = useCallback(
    (interfaceMode: InterfaceMode) => updateAccessProfile({ interfaceMode }),
    [updateAccessProfile],
  )

  const setCompanionEnabled = useCallback(
    (companionEnabled: boolean) => updateAccessProfile({ companionEnabled }),
    [updateAccessProfile],
  )

  const setParticipantPermissions = useCallback(
    (perms: AppPermission[]) => updateAccessProfile({ permissions: perms }),
    [updateAccessProfile],
  )

  const permissionsForUser = useCallback(
    (userId: string) => {
      const user = users.find((u) => u.id === userId)
      if (!user) return []
      return resolvePermissionsForUser(user, participantSpace)
    },
    [participantSpace],
  )

  const getTeamMemberAccess = useCallback(
    (userId: string): TeamMemberAccess =>
      participantSpace.teamAccess.find((a) => a.userId === userId) ?? createTeamMemberAccess(userId),
    [participantSpace],
  )

  const setTeamMemberPermissions = useCallback((userId: string, perms: AppPermission[]) => {
    const user = users.find((u) => u.id === userId)
    if (!user) return
    setState((s) => {
      const existing = s.participantSpace.teamAccess.find((a) => a.userId === userId)
      const next = accessFromPermissions(userId, user.role, perms, existing)
      return {
        ...s,
        participantSpace: {
          ...s.participantSpace,
          teamAccess: existing
            ? s.participantSpace.teamAccess.map((a) => (a.userId === userId ? next : a))
            : [...s.participantSpace.teamAccess, next],
        },
      }
    })
  }, [])

  const resetTeamMemberAccess = useCallback((userId: string) => {
    setState((s) => ({
      ...s,
      participantSpace: {
        ...s.participantSpace,
        teamAccess: s.participantSpace.teamAccess.filter((a) => a.userId !== userId),
      },
    }))
  }, [])

  const addTask = useCallback((task: TaskItem) => {
    setState((s) => ({ ...s, tasks: [...s.tasks, task] }))
  }, [])

  const toggleTaskStatus = useCallback((taskId: string) => {
    setState((s) => ({
      ...s,
      tasks: s.tasks.map((t) =>
        t.id === taskId ? { ...t, status: t.status === 'done' ? 'todo' : 'done' } : t,
      ),
    }))
  }, [])

  const addUpdate = useCallback((update: UpdatePost) => {
    setState((s) => ({ ...s, updates: [update, ...s.updates] }))
  }, [])

  const toggleChecklistItem = useCallback((updateId: string, itemId: string) => {
    setState((s) => ({
      ...s,
      updates: s.updates.map((u) =>
        u.id === updateId
          ? {
              ...u,
              checklist: u.checklist?.map((c) => (c.id === itemId ? { ...c, done: !c.done } : c)),
            }
          : u,
      ),
    }))
  }, [])

  /* --- Structured shift handover --------------------------------------- */

  const canWriteHandover = canRecordHandover(currentUser.role, permissions)

  const myLinkableShifts = useMemo(
    () => linkableShiftsFor(state.events, currentUser.id, localISODate()),
    [state.events, currentUser.id],
  )

  const eventById = useCallback(
    (eventId: string) => state.events.find((e) => e.id === eventId),
    [state.events],
  )

  /**
   * Publishing a handover writes exactly one UpdatePost. Keeping this in the
   * store rather than the form is what guarantees "saved once, shown once" —
   * the feed has no second source to read from.
   */
  const recordHandover = useCallback(
    (input: {
      note: HandoverNote
      documentationStyle: DocumentationStyle
      soap?: SoapNote
      linkedEventId?: string
      notifyBySms?: boolean
    }) => {
      if (!canWriteHandover) return false
      const text = handoverToText(input.note, input.soap)
      if (!text) return false
      // Only keep a link the worker is actually rostered on, so a stale id
      // from a re-used draft cannot attach a note to someone else's shift.
      const linkedEventId = input.linkedEventId && myLinkableShifts.some((e) => e.id === input.linkedEventId)
        ? input.linkedEventId
        : undefined

      const post: UpdatePost = {
        id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        authorId: currentUser.id,
        type: 'handover',
        text,
        createdAt: new Date().toISOString(),
        notifyBySms: input.notifyBySms ?? false,
        handover: input.note,
        documentationStyle: input.documentationStyle,
        soap: input.soap,
        linkedEventId,
      }
      setState((s) => ({ ...s, updates: [post, ...s.updates] }))
      return true
    },
    [canWriteHandover, currentUser.id, myLinkableShifts],
  )

  /**
   * Handovers are part of the one Team Updates feed, but they are the most
   * personal thing in it — so they follow view_handover, while the rest of
   * the feed follows view_updates alone.
   */
  const visibleUpdates = useMemo(() => {
    const showHandovers = canReadHandovers(permissions)
    return state.updates
      .filter((u) => (u.type === 'handover' ? showHandovers : true))
      .slice()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }, [state.updates, permissions])

  const handoverUpdates = useMemo(
    () => visibleUpdates.filter((u) => u.type === 'handover'),
    [visibleUpdates],
  )

  const addDocument = useCallback((doc: DocumentItem) => {
    setState((s) => ({ ...s, documents: [doc, ...s.documents] }))
  }, [])

  const updateDocumentAccess = useCallback(
    (docId: string, access: DocumentItem['access'], allowedUserIds: string[]) => {
      setState((s) => ({
        ...s,
        documents: s.documents.map((d) => (d.id === docId ? { ...d, access, allowedUserIds } : d)),
      }))
    },
    [],
  )

  const updateGoalProgress = useCallback((goalId: string, progressPercent: number) => {
    setState((s) => ({
      ...s,
      goals: s.goals.map((g) =>
        g.id === goalId ? { ...g, progressPercent, updatedAt: new Date().toISOString() } : g,
      ),
    }))
  }, [])

  const updateNotificationPrefs = useCallback((prefs: NotificationPrefs) => {
    setState((s) => ({ ...s, notificationPrefs: prefs }))
  }, [])

  const logSms = useCallback((entry: SmsLogEntry) => {
    setState((s) => ({ ...s, smsLog: [entry, ...s.smsLog] }))
  }, [])

  const canSeeDocument = useCallback(
    (doc: DocumentItem) => {
      const uploaderRole = users.find((u) => u.id === doc.uploadedByUserId)?.role
      return canAccessDocument(currentUser, doc, permissions, uploaderRole)
    },
    [currentUser, permissions],
  )

  const sendMessage = useCallback(
    (partnerId: string, text: string, eventId?: string, coverRequestId?: string) => {
      if (!text.trim()) return
      const message: ChatMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        threadId: threadIdFor(currentUser.id, partnerId),
        senderId: currentUser.id,
        text: text.trim(),
        createdAt: new Date().toISOString(),
        readBy: [currentUser.id],
        eventId,
        coverRequestId,
      }
      setState((s) => ({ ...s, messages: [...s.messages, message] }))
    },
    [currentUser],
  )

  const addEvent = useCallback(
    (event: ScheduleEvent) => {
      setState((s) => ({ ...s, events: [...s.events, event] }))
      const label = niceDateLabel(event.date)
      event.assignees.forEach((a) => {
        if (a.userId === currentUser.id) return
        sendMessage(
          a.userId,
          `📅 You've been assigned "${event.title}" (${typeMeta[event.type].label}) on ${label}, ${event.startTime}–${event.endTime}. Please accept or decline below.${event.notes ? ` Note: ${event.notes}` : ''}`,
          event.id,
        )
      })
    },
    [currentUser, sendMessage],
  )

  const respondToEvent = useCallback(
    (eventId: string, status: 'accepted' | 'declined') => {
      const event = state.events.find((e) => e.id === eventId)
      if (!event) return
      setState((s) => ({
        ...s,
        events: s.events.map((e) =>
          e.id === eventId
            ? { ...e, assignees: e.assignees.map((a) => (a.userId === currentUser.id ? { ...a, status } : a)) }
            : e,
        ),
      }))
      if (event.createdByUserId !== currentUser.id) {
        const icon = status === 'accepted' ? '✅' : '❌'
        const verb = status === 'accepted' ? 'accepted' : 'declined'
        sendMessage(
          event.createdByUserId,
          `${icon} ${currentUser.name} ${verb} "${event.title}" on ${event.date}, ${event.startTime}–${event.endTime}.`,
        )
      }
    },
    [state.events, currentUser, sendMessage],
  )

  const updateEvent = useCallback(
    (eventId: string, changes: EventUpdateChanges) => {
      const event = state.events.find((e) => e.id === eventId)
      if (!event) return

      const { assigneeIds, ...fieldChanges } = changes
      let newAssignees: EventAssignee[] = event.assignees
      let addedIds: string[] = []
      let removedIds: string[] = []

      if (assigneeIds) {
        const prevIds = event.assignees.map((a) => a.userId)
        addedIds = assigneeIds.filter((id) => !prevIds.includes(id))
        removedIds = prevIds.filter((id) => !assigneeIds.includes(id))
        newAssignees = assigneeIds.map((id) => event.assignees.find((a) => a.userId === id) ?? { userId: id, status: 'pending' })
      }

      // Assigning someone by hand answers a Needs Cover shift just as an
      // accepted cover request does — the history of the withdrawal is kept.
      const filledByHand = needsCover(event) && newAssignees.length > 0
      const cover = filledByHand
        ? {
            ...event.cover!,
            status: 'covered' as const,
            filledByUserId: newAssignees[0].userId,
            filledAt: new Date().toISOString(),
          }
        : event.cover

      const updated: ScheduleEvent = { ...event, ...fieldChanges, assignees: newAssignees, cover }
      setState((s) => ({
        ...s,
        events: s.events.map((e) => (e.id === eventId ? updated : e)),
        coverRequests: filledByHand
          ? s.coverRequests.map((r) =>
              r.eventId === eventId && r.status === 'pending' ? { ...r, status: 'closed' as const } : r,
            )
          : s.coverRequests,
      }))

      const label = niceDateLabel(updated.date)

      addedIds.forEach((id) => {
        if (id === currentUser.id) return
        sendMessage(
          id,
          `📅 You've been added to "${updated.title}" (${typeMeta[updated.type].label}) on ${label}, ${updated.startTime}–${updated.endTime}. Please accept or decline below.${updated.notes ? ` Note: ${updated.notes}` : ''}`,
          updated.id,
        )
      })
      removedIds.forEach((id) => {
        if (id === currentUser.id) return
        sendMessage(id, `🚫 ${currentUser.name} removed you from "${updated.title}" on ${label}.`)
      })

      const detailFieldsChanged = Object.keys(fieldChanges).length > 0
      if (detailFieldsChanged) {
        newAssignees
          .map((a) => a.userId)
          .filter((id) => !addedIds.includes(id) && id !== currentUser.id)
          .forEach((id) => {
            sendMessage(
              id,
              `✏️ ${currentUser.name} updated "${updated.title}" — now ${label}, ${updated.startTime}–${updated.endTime}.${updated.notes ? ` Note: ${updated.notes}` : ''}`,
            )
          })
      }
    },
    [state.events, currentUser, sendMessage],
  )

  const deleteEvent = useCallback(
    (eventId: string) => {
      const event = state.events.find((e) => e.id === eventId)
      setState((s) => ({
        ...s,
        events: s.events.filter((e) => e.id !== eventId),
        coverRequests: s.coverRequests.filter((r) => r.eventId !== eventId),
      }))
      if (event) {
        event.assignees.forEach((a) => {
          if (a.userId === currentUser.id) return
          sendMessage(a.userId, `🗑️ ${currentUser.name} cancelled "${event.title}" on ${event.date}, ${event.startTime}–${event.endTime}.`)
        })
      }
    },
    [state.events, currentUser, sendMessage],
  )

  /* --- Support worker availability & Needs Cover ------------------------ *
   * A withdrawal never deletes the shift. It hands the shift back to the
   * participant flagged as Needs Cover, and nobody is assigned again until a
   * team member has actually said yes.                                      */

  /**
   * Everyone the participant / nominee should be told about a shift change:
   * whoever booked it, the participant themselves, and anyone they have
   * trusted with arranging cover.
   */
  const shiftOwnerIds = useCallback(
    (event: ScheduleEvent) =>
      Array.from(
        new Set([
          event.createdByUserId,
          participantSpace.participantUserId,
          ...users.filter((u) => permissionsForUser(u.id).includes('manage_cover')).map((u) => u.id),
        ]),
      ).filter((id) => id !== currentUser.id),
    [participantSpace.participantUserId, currentUser.id, permissionsForUser],
  )

  const withdrawFromShift = useCallback(
    (eventId: string, reason?: string) => {
      if (currentUser.role !== 'support_worker') return
      const event = state.events.find((e) => e.id === eventId)
      if (!event || event.type !== 'shift') return
      if (!event.assignees.some((a) => a.userId === currentUser.id)) return

      const withdrawnAt = new Date().toISOString()
      const trimmedReason = reason?.trim() || undefined

      setState((s) => ({
        ...s,
        events: s.events.map((e) =>
          e.id === eventId
            ? {
                ...e,
                assignees: e.assignees.filter((a) => a.userId !== currentUser.id),
                cover: {
                  status: 'needs_cover' as const,
                  originalAssigneeUserId: currentUser.id,
                  withdrawnAt,
                  reason: trimmedReason,
                },
              }
            : e,
        ),
        // Anything still open from an earlier round of cover no longer applies.
        coverRequests: s.coverRequests.map((r) =>
          r.eventId === eventId && r.status === 'pending' ? { ...r, status: 'closed' as const } : r,
        ),
      }))

      shiftOwnerIds(event).forEach((id) => {
        sendMessage(
          id,
          `⚠️ ${currentUser.name} can no longer do "${event.title}" on ${shiftDateLabel(event.date)}, ${event.startTime}–${event.endTime}. The shift now needs cover.${trimmedReason ? ` Reason: ${trimmedReason}` : ''}`,
        )
      })
    },
    [state.events, currentUser, sendMessage, shiftOwnerIds],
  )

  const sendCoverRequests = useCallback(
    (eventId: string, userIds: string[], note?: string) => {
      const event = state.events.find((e) => e.id === eventId)
      if (!event || !needsCover(event)) return

      const trimmedNote = note?.trim() || undefined
      const createdAt = new Date().toISOString()
      const recipients = Array.from(new Set(userIds)).filter(
        (id) =>
          id !== currentUser.id &&
          isSupportWorkerUser(id) &&
          permissionsForUser(id).includes('cover_shifts') &&
          // One live ask per person per shift — re-asking waits for a reply.
          !state.coverRequests.some(
            (r) => r.eventId === eventId && r.requestedUserId === id && r.status === 'pending',
          ),
      )
      if (recipients.length === 0) return

      const requests: CoverRequest[] = recipients.map((requestedUserId, index) => ({
        id: `cov-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
        eventId,
        requestedByUserId: currentUser.id,
        requestedUserId,
        status: 'pending',
        createdAt,
        note: trimmedNote,
      }))

      setState((s) => ({ ...s, coverRequests: [...s.coverRequests, ...requests] }))

      requests.forEach((request) => {
        sendMessage(
          request.requestedUserId,
          `🆘 Can you cover this shift? ${shiftSummary(event)}.${event.notes ? ` Details: ${event.notes}` : ''}${trimmedNote ? ` ${currentUser.name} says: ${trimmedNote}` : ''} Being available doesn't book you in — please accept or decline.`,
          undefined,
          request.id,
        )
      })
    },
    [state.events, state.coverRequests, currentUser, sendMessage, permissionsForUser],
  )

  const respondToCoverRequest = useCallback(
    (requestId: string, response: 'accepted' | 'declined') => {
      if (currentUser.role !== 'support_worker') return
      const request = state.coverRequests.find((r) => r.id === requestId)
      if (!request || request.status !== 'pending') return
      if (request.requestedUserId !== currentUser.id) return
      const event = state.events.find((e) => e.id === request.eventId)
      if (!event) return

      const respondedAt = new Date().toISOString()
      // Whether this accept actually wins the shift is decided against the
      // freshest state inside the updater, so two accepts can never both land.
      const stillOpen = needsCover(event)
      const accepting = response === 'accepted' && stillOpen

      setState((s) => {
        const live = s.coverRequests.find((r) => r.id === requestId)
        const liveEvent = s.events.find((e) => e.id === request.eventId)
        if (!live || live.status !== 'pending' || !liveEvent) return s

        if (response === 'declined') {
          return {
            ...s,
            coverRequests: s.coverRequests.map((r) =>
              r.id === requestId ? { ...r, status: 'declined' as const, respondedAt } : r,
            ),
          }
        }

        // Someone else already covered it — the ask is closed, not accepted.
        if (!needsCover(liveEvent)) {
          return {
            ...s,
            coverRequests: s.coverRequests.map((r) =>
              r.id === requestId ? { ...r, status: 'closed' as const, respondedAt } : r,
            ),
          }
        }

        return {
          ...s,
          events: s.events.map((e) =>
            e.id === request.eventId
              ? {
                  ...e,
                  assignees: [
                    ...e.assignees.filter((a) => a.userId !== currentUser.id),
                    { userId: currentUser.id, status: 'accepted' as const },
                  ],
                  cover: {
                    ...e.cover!,
                    status: 'covered' as const,
                    filledByUserId: currentUser.id,
                    filledAt: respondedAt,
                  },
                }
              : e,
          ),
          coverRequests: s.coverRequests.map((r) => {
            if (r.id === requestId) return { ...r, status: 'accepted' as const, respondedAt }
            // Every other open ask for this shift is now filled, not pending.
            if (r.eventId === request.eventId && r.status === 'pending') {
              return { ...r, status: 'closed' as const, respondedAt }
            }
            return r
          }),
        }
      })

      if (response === 'declined') {
        sendMessage(
          request.requestedByUserId,
          `❌ ${currentUser.name} can't cover "${event.title}" on ${shiftDateLabel(event.date)}, ${event.startTime}–${event.endTime}. The shift still needs cover.`,
        )
        return
      }

      if (!accepting) {
        sendMessage(
          request.requestedByUserId,
          `ℹ️ ${currentUser.name} tried to accept "${event.title}" on ${shiftDateLabel(event.date)}, but it was already covered by someone else.`,
        )
        return
      }

      sendMessage(
        request.requestedByUserId,
        `✅ ${currentUser.name} accepted the cover request for "${event.title}" on ${shiftDateLabel(event.date)}, ${event.startTime}–${event.endTime}. They're now the assigned support worker.`,
      )
      // Let everyone else who was asked stand down.
      state.coverRequests
        .filter((r) => r.eventId === request.eventId && r.status === 'pending' && r.id !== requestId)
        .forEach((r) => {
          sendMessage(
            r.requestedUserId,
            `👍 Thanks — "${event.title}" on ${shiftDateLabel(event.date)} is now covered by ${currentUser.name}. Nothing more needed from you.`,
          )
        })
    },
    [state.coverRequests, state.events, currentUser, sendMessage],
  )

  const coverRequestsForEvent = useCallback(
    (eventId: string) =>
      state.coverRequests
        .filter((r) => r.eventId === eventId)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [state.coverRequests],
  )

  const pendingCoverRequestsFor = useCallback(
    (userId: string) =>
      state.coverRequests
        .filter((r) => r.requestedUserId === userId && r.status === 'pending')
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [state.coverRequests],
  )

  const coverCandidatesForEvent = useCallback(
    (eventId: string): CoverCandidate[] => {
      const event = state.events.find((e) => e.id === eventId)
      if (!event) return []
      return buildCoverCandidates({
        event,
        // Cover candidates are support workers already in this SCHub space.
        // Allied health, coordinators, participants and nominees are never
        // part of support-shift availability or cover.
        teamMembers: users.filter((u) => u.role === 'support_worker'),
        events: state.events,
        coverRequests: state.coverRequests,
        availabilityFor: (userId) =>
          isSupportWorkerUser(userId) ? state.availability.find((a) => a.userId === userId) : undefined,
        permissionsFor: permissionsForUser,
      })
    },
    [state.events, state.coverRequests, state.availability, permissionsForUser],
  )

  const markThreadRead = useCallback(
    (partnerId: string) => {
      const threadId = threadIdFor(currentUser.id, partnerId)
      setState((s) => ({
        ...s,
        messages: s.messages.map((m) =>
          m.threadId === threadId && !m.readBy.includes(currentUser.id)
            ? { ...m, readBy: [...m.readBy, currentUser.id] }
            : m,
        ),
      }))
    },
    [currentUser],
  )

  const unreadCountFrom = useCallback(
    (partnerId: string) => {
      const threadId = threadIdFor(currentUser.id, partnerId)
      return state.messages.filter(
        (m) => m.threadId === threadId && m.senderId === partnerId && !m.readBy.includes(currentUser.id),
      ).length
    },
    [state.messages, currentUser],
  )

  const createGroupThread = useCallback(
    (name: string, participantIds: string[]) => {
      const id = `grp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      const group: GroupThread = {
        id,
        name,
        participantIds: Array.from(new Set([...participantIds, currentUser.id])),
        createdByUserId: currentUser.id,
        createdAt: new Date().toISOString(),
      }
      setState((s) => ({ ...s, groups: [...s.groups, group] }))
      return id
    },
    [currentUser],
  )

  const sendGroupMessage = useCallback(
    (groupId: string, text: string, eventId?: string) => {
      if (!text.trim()) return
      const message: ChatMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        threadId: groupId,
        senderId: currentUser.id,
        text: text.trim(),
        createdAt: new Date().toISOString(),
        readBy: [currentUser.id],
        eventId,
      }
      setState((s) => ({ ...s, messages: [...s.messages, message] }))
    },
    [currentUser],
  )

  const markGroupThreadRead = useCallback(
    (groupId: string) => {
      setState((s) => ({
        ...s,
        messages: s.messages.map((m) =>
          m.threadId === groupId && !m.readBy.includes(currentUser.id)
            ? { ...m, readBy: [...m.readBy, currentUser.id] }
            : m,
        ),
      }))
    },
    [currentUser],
  )

  const unreadCountForGroup = useCallback(
    (groupId: string) => {
      return state.messages.filter(
        (m) => m.threadId === groupId && m.senderId !== currentUser.id && !m.readBy.includes(currentUser.id),
      ).length
    },
    [state.messages, currentUser],
  )

  const totalUnreadCount = useMemo(
    () =>
      state.messages.filter((m) => {
        if (m.senderId === currentUser.id) return false
        if (m.readBy.includes(currentUser.id)) return false
        const participants = isGroupThreadId(m.threadId)
          ? state.groups.find((g) => g.id === m.threadId)?.participantIds ?? []
          : m.threadId.split('__')
        return participants.includes(currentUser.id)
      }).length,
    [state.messages, state.groups, currentUser],
  )

  const getAvailability = useCallback(
    (userId: string): AvailabilityPrefs => {
      if (!isSupportWorkerUser(userId)) {
        return { userId, availableDays: [], showOnCalendar: false }
      }
      return (
        state.availability.find((a) => a.userId === userId) ?? {
          userId,
          availableDays: [],
          showOnCalendar: false,
        }
      )
    },
    [state.availability],
  )

  const updateAvailability = useCallback((prefs: AvailabilityPrefs) => {
    if (!isSupportWorkerUser(prefs.userId)) return
    const stamped: AvailabilityPrefs = { ...prefs, updatedAt: new Date().toISOString() }
    setState((s) => {
      const supportWorkerAvailability = s.availability.filter((a) => isSupportWorkerUser(a.userId))
      const exists = supportWorkerAvailability.some((a) => a.userId === prefs.userId)
      return {
        ...s,
        availability: exists
          ? supportWorkerAvailability.map((a) => (a.userId === prefs.userId ? stamped : a))
          : [...supportWorkerAvailability, stamped],
      }
    })
  }, [])

  const isAvailableOn = useCallback(
    (userId: string, dateISO: string) => {
      if (!isSupportWorkerUser(userId)) return false
      return isAvailableOnDate(
        state.availability.find((a) => a.userId === userId),
        dateISO,
      )
    },
    [state.availability],
  )

  /** Support workers who have shared availability for a day — the green dot. */
  const availableTeamMembersOn = useCallback(
    (dateISO: string) =>
      users.filter((u) => u.role === 'support_worker' && isAvailableOn(u.id, dateISO)),
    [isAvailableOn],
  )

  const value: AppContextValue = {
    ...state,
    currentUser,
    allUsers: isPrivateBeta ? users.filter(user => betaSameSupportCircle(currentUser.id, user.id)) : users,
    setCurrentUserId,
    addEvent,
    addTask,
    toggleTaskStatus,
    addUpdate,
    toggleChecklistItem,
    myLinkableShifts,
    recordHandover,
    canWriteHandover,
    visibleUpdates,
    handoverUpdates,
    eventById,
    addDocument,
    updateDocumentAccess,
    updateGoalProgress,
    updateNotificationPrefs,
    logSms,
    canSeeDocument,
    sendMessage,
    markThreadRead,
    unreadCountFrom,
    totalUnreadCount,
    getAvailability,
    updateAvailability,
    isAvailableOn,
    availableTeamMembersOn,
    respondToEvent,
    updateEvent,
    deleteEvent: isPrivateBeta ? () => {} : deleteEvent,
    withdrawFromShift,
    sendCoverRequests,
    respondToCoverRequest,
    coverRequestsForEvent,
    pendingCoverRequestsFor,
    coverCandidatesForEvent,
    createGroupThread,
    sendGroupMessage,
    markGroupThreadRead,
    unreadCountForGroup,
    participantSpace,
    accessProfile,
    permissions,
    can,
    isSpaceOwner,
    interfaceMode: accessProfile.interfaceMode,
    companionEnabled: accessProfile.companionEnabled,
    setAccountType,
    setInterfaceMode,
    setCompanionEnabled,
    setParticipantPermissions: isPrivateBeta ? () => {} : setParticipantPermissions,
    permissionsForUser,
    getTeamMemberAccess,
    setTeamMemberPermissions: isPrivateBeta ? () => {} : setTeamMemberPermissions,
    resetTeamMemberAccess: isPrivateBeta ? () => {} : resetTeamMemberAccess,
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppProvider')
  return ctx
}

export function useIsCoordinatorLike() {
  const { currentUser } = useApp()
  return currentUser.role === 'participant' || currentUser.role === 'rep'
}

/** Convenience hook for gating a single piece of UI. */
export function usePermission(permission: AppPermission) {
  const { permissions } = useApp()
  return hasPermission(permissions, permission)
}

